import LegalPageLayout from '../../components/feature/LegalPageLayout';

const SITE = 'https://blueglobemedia.com';
const EMAIL = 'info@blueglobemedia.com';

export default function TermsPage() {
  return (
    <LegalPageLayout
      badge="Legal"
      title="Terms & Conditions"
      subtitle="These terms govern your use of the Blue Globe Media website and our digital marketing services. Please read them carefully before engaging with us."
      lastUpdated="April 4, 2026"
      seo={{
        title: 'Terms & Conditions | Blue Globe Media',
        description: 'Read the Blue Globe Media terms and conditions. Understand the rules for using our website and engaging our digital marketing services in India.',
        keywords: 'terms and conditions, terms of use, Blue Globe Media legal, service agreement',
        canonicalUrl: `${SITE}/terms`,
      }}
      sections={[
        {
          title: '1. Agreement to Terms',
          content: (
            <p>
              By accessing or using{' '}
              <a href={SITE} className="text-brand hover:underline">{SITE}</a> (&quot;Website&quot;) or engaging
              Blue Globe Media (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) for services, you agree to be
              bound by these Terms &amp; Conditions (&quot;Terms&quot;). If you do not agree, please do not use our
              Website or services.
            </p>
          ),
        },
        {
          title: '2. About Our Services',
          content: (
            <>
              <p>
                Blue Globe Media provides digital marketing services including, but not limited to, search engine
                optimization (SEO), performance marketing, social media marketing, media buying, paid advertising,
                branding, and website development.
              </p>
              <p>
                Specific deliverables, timelines, fees, and scope will be defined in a separate proposal, statement
                of work, or service agreement. In the event of a conflict, the signed service agreement takes
                precedence over these Terms.
              </p>
            </>
          ),
        },
        {
          title: '3. Use of the Website',
          content: (
            <>
              <p>You agree to use our Website only for lawful purposes. You must not:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Use the Website in any way that violates applicable laws or regulations</li>
                <li>Attempt to gain unauthorised access to our systems, servers, or data</li>
                <li>Transmit malware, spam, or harmful code through our contact forms or communications</li>
                <li>Copy, scrape, or reproduce Website content without our written permission</li>
                <li>Misrepresent your identity or affiliation when contacting us</li>
              </ul>
            </>
          ),
        },
        {
          title: '4. Enquiries & Communications',
          content: (
            <p>
              Submitting a contact form or enquiry does not create a client relationship or obligation on our part
              to provide services. We reserve the right to decline projects that are outside our scope, capacity, or
              alignment with our business values. Any information you submit must be accurate and not infringe
              third-party rights.
            </p>
          ),
        },
        {
          title: '5. Client Responsibilities',
          content: (
            <>
              <p>When you engage us as a client, you agree to:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Provide timely access to accounts, assets, and information required to deliver services</li>
                <li>Ensure you have the rights to any content, logos, or materials you supply</li>
                <li>Review and approve deliverables within agreed timeframes</li>
                <li>Pay invoices according to the agreed payment schedule</li>
                <li>Comply with applicable advertising, data protection, and platform policies</li>
              </ul>
            </>
          ),
        },
        {
          title: '6. Fees & Payment',
          content: (
            <>
              <p>
                Service fees, billing cycles, and payment terms will be specified in your proposal or service
                agreement. Unless otherwise stated, invoices are due within the period specified on the invoice.
              </p>
              <p>
                Late payments may result in suspension of services. Third-party costs (such as ad spend, software
                subscriptions, or media purchases) are typically billed separately unless included in a fixed package.
              </p>
            </>
          ),
        },
        {
          title: '7. Intellectual Property',
          content: (
            <>
              <p>
                All content on this Website — including text, graphics, logos, layouts, and design — is owned by or
                licensed to Blue Globe Media and protected by applicable intellectual property laws. You may not
                reproduce or distribute Website content without prior written consent.
              </p>
              <p>
                Upon full payment, clients receive rights to final deliverables as defined in the service agreement.
                We retain the right to showcase completed work in our portfolio and marketing materials unless
                otherwise agreed in writing.
              </p>
            </>
          ),
        },
        {
          title: '8. Results & Performance Disclaimer',
          content: (
            <p>
              Digital marketing outcomes depend on many factors beyond our control, including market conditions,
              competition, platform algorithm changes, and client cooperation. While we apply professional expertise
              and data-driven strategies, we do not guarantee specific rankings, traffic levels, leads, sales, or
              return on investment unless explicitly stated in a signed agreement.
            </p>
          ),
        },
        {
          title: '9. Confidentiality',
          content: (
            <p>
              We treat client business information shared during engagements as confidential and use it only to
              deliver services. Clients agree to keep our proprietary methodologies, internal processes, and
              non-public pricing confidential unless disclosure is required by law.
            </p>
          ),
        },
        {
          title: '10. Limitation of Liability',
          content: (
            <>
              <p>
                To the fullest extent permitted by law, Blue Globe Media shall not be liable for any indirect,
                incidental, special, consequential, or punitive damages arising from your use of the Website or our
                services.
              </p>
              <p>
                Our total liability for any claim related to services shall not exceed the fees paid by you to Blue
                Globe Media for the specific service giving rise to the claim during the three (3) months preceding
                the claim.
              </p>
            </>
          ),
        },
        {
          title: '11. Termination',
          content: (
            <p>
              Either party may terminate a service engagement according to the terms in the applicable service
              agreement. We may suspend or terminate Website access or services immediately if you breach these Terms,
              fail to make payment, or engage in conduct that harms our business or reputation.
            </p>
          ),
        },
        {
          title: '12. Third-Party Platforms',
          content: (
            <p>
              Our services may involve third-party platforms such as Google, Meta, LinkedIn, or other advertising and
              analytics tools. Your use of those platforms is subject to their respective terms and policies. We are
              not responsible for changes, outages, or actions taken by third-party providers.
            </p>
          ),
        },
        {
          title: '13. Governing Law',
          content: (
            <p>
              These Terms are governed by the laws of India. Any disputes arising from these Terms or our services
              shall be subject to the exclusive jurisdiction of the courts in Mumbai, Maharashtra, India, unless
              otherwise agreed in writing.
            </p>
          ),
        },
        {
          title: '14. Changes to These Terms',
          content: (
            <p>
              We may update these Terms at any time. Updated Terms will be posted on this page with a revised
              &quot;Last updated&quot; date. Your continued use of the Website after changes are posted constitutes
              acceptance of the updated Terms.
            </p>
          ),
        },
        {
          title: '15. Contact Us',
          content: (
            <p>
              For questions about these Terms, contact us at{' '}
              <a href={`mailto:${EMAIL}`} className="text-brand hover:underline">{EMAIL}</a> or visit{' '}
              <a href={`${SITE}/contact`} className="text-brand hover:underline">{SITE}/contact</a>.
            </p>
          ),
        },
      ]}
    />
  );
}
